import ModalComponent from 'components/Common/Modal';
import Render from 'components/Common/Render';

import BusinessHeader from 'components/Home/BusinessHeader';
import TopupDetails from 'components/Home/TopupDetails';
import {
  rewardsSummary,
  businessPlans,
  businessOverallStatsApi,
  userFeedbackApi,
} from 'constants/apiUrls';
import {
  FETCH_STATES,
  PlanActivationStatus,
  PreSignupStatusEnum,
  StripeSessionPaymentStatus,
} from 'enum/common';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { http } from 'middleware/apiMiddleware';
import { useCallback, useEffect, useMemo, useState } from 'react';
import BusinessStats from 'components/Home/BusinessStats';
import { RootState } from 'store';
import { useAppSelector } from 'hooks/useRedux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLazyBusinessQuery } from 'store/apis/businessApi';
import { useLazyBusinessListQuery } from 'store/apis/businessListApi';
import { setBusiness, setBusinesses } from 'store/slices/businessSlice';
import { useDispatch } from 'react-redux';
import { checkIsAnywhereCommunity } from 'utils/common';
import UserFeedbackTiles from 'components/Home/UserFeedbackTiles';
import TransferConfirmationModal, {
  ITransferFormValues,
} from 'components/Home/TransferConfirmationModal';
import { Loader } from 'components/Common/Loader';
import { usePusher } from 'hooks/usePusher';
import useMixpanel from 'hooks/useMixpanel';
import { routes } from 'constants/routes';
import { useLazyDefaultAccountQuery } from 'store/apis/defaultAccountApi';
import { setDefaultAccount } from 'store/slices/defaultAccountSlice';

export type InstantPayModalDataType = {
  moduleId: string;
} | null;

const Home = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldShowinstantPayTutorial = location.state?.activateInstantPay;
  const fromOnboarding = location.state?.fromOnboarding;
  const businessUuidFromOnboarding = location.state?.businessUuid;
  const businessDataFromOnboarding = location.state?.businessData;

  const [_shouldShowPreSignup, setShouldShowPreSignup] = useState<boolean>(false);
  const [_isPreRegisterationSuccessfull, setIsPreRegisterationSuccessfull] = useState(false);
  const [_activateOfferModal, setActivateOfferModal] = useState<ActivateOfferModalType>(null);
  const _isPaymentSucecss = useMemo(
    () => searchParams.get('paymentStatus') === StripeSessionPaymentStatus.SUCCESS,
    [searchParams],
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackEvent } = useMixpanel();

  const [selectedBusiness, setSelectedBusiness] = useLocalStorage('selectedBusiness', null);
  const [fetchBusienss, { isFetching }] = useLazyBusinessQuery();
  const [fetchBusinessList, { data: businessListData, isSuccess: isBusinessListSuccess }] =
    useLazyBusinessListQuery();
  const [fetchDefaultAccount, { data: defaultPaymentMethod, isSuccess: isAccountsetupSuccess }] =
    useLazyDefaultAccountQuery({});
  const [showInstantpayTutorial, setShowInstantpayTutorial] =
    useState<InstantPayModalDataType>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingAmount, setUpdatingAmount] = useState(false);
  const [deActivatingOffer, setDeactivatingOffer] = useState(false);
  const [transferringAmount, setTransferringAmount] = useState(false);
  const [silentUpdate, setSilentUpdate] = useState(false);
  const [preSignupHeader, setPreSignupHeader] = useState('Pre-registration');
  const [showDeactiveConfirmDialog, setShowDeactiveConfirmDialog] =
    useState<BusinessPlanType | null>(null);
  const [businessStats, setBusinessStats] = useState<{
    status: FETCH_STATES;
    data: BusinessStatsType;
  }>({ status: FETCH_STATES.LOADING, data: null });
  const [plans, setPlans] = useState<{
    status: FETCH_STATES;
    data: BusinessPlanType[];
  }>({ status: FETCH_STATES.LOADING, data: [] });
  const [businessOverallStats, setBusinessOverallStats] = useState<{
    status: FETCH_STATES;
    data: BusinessOverallStatsType | null;
  }>({ status: FETCH_STATES.LOADING, data: null });
  const [userFeedback, setUserFeedback] = useState<{
    status: FETCH_STATES;
    data: Array<UserFeedbackType> | null;
  }>({ status: FETCH_STATES.LOADING, data: null });
  const [showTransferBalanceConfirmDialog, setShowTransferBalanceConfirmDialog] = useState<{
    transferAmount: number;
  } | null>(null);

  const businessState = useAppSelector((state: RootState) => state.businesses);
  const business: BusinessType = businessState?.business || selectedBusiness || null;
  const preSignupStatus = useMemo(
    () => business?.preSignupDetails?.preSignupStatus,
    [business?.preSignupDetails?.preSignupStatus],
  );

  const isAnyBusinessActivity =
    !!businessStats?.data?.totalRedeemAmount ||
    !!businessStats?.data?.totalRewardsDistributed ||
    !!businessStats?.data?.totalTransactionAmount;

  const removeSearchParams = () => {
    if (searchParams.has('paymentStatus')) {
      searchParams.delete('paymentStatus');
      setSearchParams(searchParams);
    }
  };

  const _onPaymentSucecssModalClose = () => {
    removeSearchParams();
  };

  const _onPreRegisterClick = () => {
    setIsPreRegisterationSuccessfull(false);
    setShouldShowPreSignup(true);
  };
  const _onLearnMoreClick = () => {
    setIsPreRegisterationSuccessfull(false);
    setShouldShowPreSignup(true);
  };

  const fetchBusinessStats = async (businessUuid: string) => {
    try {
      setBusinessStats({ status: FETCH_STATES.LOADING, data: null });
      const { data, error } = await http.get(rewardsSummary(businessUuid));
      if (error) {
        setBusinessStats({ status: FETCH_STATES.FAILED, data: null });
      }
      setBusinessStats({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setBusinessStats({ status: FETCH_STATES.FAILED, data: null });
    }
  };

  const fetchPromotionPlans = async (businessUuid: string) => {
    try {
      setPlans({ status: FETCH_STATES.LOADING, data: [] });
      const { data, error } = await http.get(businessPlans(businessUuid));
      if (error) {
        setPlans({ status: FETCH_STATES.FAILED, data: [] });
      }
      setPlans({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setPlans({ status: FETCH_STATES.FAILED, data: [] });
    }
  };

  const fetchBusinessOverallStats = async () => {
    if (!business?.uuid) return;

    try {
      setBusinessOverallStats({ status: FETCH_STATES.LOADING, data: null });
      const { data, error } = await http.get(businessOverallStatsApi(business?.uuid));
      if (error) {
        setBusinessOverallStats({ status: FETCH_STATES.FAILED, data: null });
      }
      setBusinessOverallStats({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setBusinessOverallStats({ status: FETCH_STATES.FAILED, data: null });
    }
  };

  const fetchUserFeedbacks = async () => {
    if (!business?.uuid) return;

    try {
      setUserFeedback({ status: FETCH_STATES.LOADING, data: null });
      const { data, error } = await http.get(userFeedbackApi(business?.uuid));
      if (error) {
        setUserFeedback({ status: FETCH_STATES.FAILED, data: null });
      }
      setUserFeedback({ status: FETCH_STATES.SUCECSS, data });
    } catch {
      setUserFeedback({ status: FETCH_STATES.FAILED, data: null });
    }
  };

  const _activateOffer = (moduleId: string, topUpAmount: number, referralCode: string) => {
    setActivateOfferModal({
      moduleId,
      topupAmount: topUpAmount,
      businessId: business?.uuid,
      referralCode,
    });
  };

  const activateInstantPay = (moduleId: string) => {
    setShowInstantpayTutorial({
      moduleId,
    });
  };

  const _activateSubscription = async (moduleId: string) => {
    try {
      trackEvent('activate_subscription_button_Clicking', {
        planId: moduleId,
      });

      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${moduleId}`,
        {
          enabled: true,
        },
      );
      if (!!data && !error) {
        toast.success('Subscription activated successfully');
        trackEvent('activate_subscription_success', {
          planId: moduleId,
        });
        setBusinessDetails();
      } else {
        toast.error('Failed to activate subscription');
        trackEvent('activate_subscription_failure', {
          planId: moduleId,
        });
      }
    } catch (error) {
      toast.error('Unexpected error occurred while activating subscription');
      trackEvent('activate_subscription_failure', {
        planId: moduleId,
      });
    }
  };

  const _onDeactivationConfig = async () => {
    try {
      setDeactivatingOffer(true);

      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${showDeactiveConfirmDialog?.uuid}`,
        {
          enabled: false,
        },
      );
      if (!!data && !error) {
        toast.success('Plan deactivated successfully');
      }
      trackEvent('deactivate_offer_success', {
        planId: showDeactiveConfirmDialog?.uuid ?? '',
      });
      setShowDeactiveConfirmDialog(null);
      setBusinessDetails();
    } catch (error) {
      trackEvent('deactivate_offer_failure', {
        planId: showDeactiveConfirmDialog?.uuid ?? '',
      });
      setShowDeactiveConfirmDialog(null);
      toast.error('Unexpected error occurred while deactivating plan');
    } finally {
      setShowDeactiveConfirmDialog(null);
      setDeactivatingOffer(false);
    }
  };

  const _deActivateOffer = async (businessPlan: BusinessPlanType) => {
    trackEvent('deactivate_offer_button_Clicking', {
      planId: businessPlan?.uuid,
    });
    setShowDeactiveConfirmDialog(businessPlan);
  };

  const onTransferConfirm = async (values: ITransferFormValues) => {
    try {
      setTransferringAmount(true);

      const { error } = await http.post(`/business/${business?.uuid}/request-payout`, {
        requestedAmount: Number(values.transferAmount),
      });

      if (!error) {
        toast.success('Balance transfer request submitted successfully');
        setBusinessDetails();
      } else {
        const errorMessage = error?.error[0];
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Unexpected error occurred while raising transfer request');
    } finally {
      setShowTransferBalanceConfirmDialog(null);
      setTransferringAmount(false);
    }
  };

  const _updateTopupAmount = async (moduleId: string, topUpAmount: number) => {
    try {
      setUpdatingAmount(true);
      const { data, error } = await http.patch(
        `/business/${business?.uuid}/business-paid-modules/${moduleId}`,
        {
          enabled: true,
          formData: { autoTopUpAmount: topUpAmount },
        },
      );
      if (!!data && !error) {
        toast.success('Topup amount updated successfully');
        trackEvent('update_topup_amount_success', {
          planId: moduleId,
          topUpAmount,
        });
      }
    } catch (error) {
      trackEvent('update_topup_amount_failure', {
        planId: moduleId,
        topUpAmount,
        error,
      });
      toast.error('Unexpected error occurred while updating topup amount');
    } finally {
      setUpdatingAmount(false);
    }
  };

  const setBusinessDetails = async () => {
    if (!business?.uuid) {
      navigate(`${routes.chooseBusiness}/1`);
      return;
    }

    try {
      let updatedData: BusinessType | undefined = business;
      const { data, isError, error } = await fetchBusienss({
        uuid: business?.uuid,
      });
      // @ts-ignore
      const errorStatus = error?.originalStatus;

      if (isError && errorStatus === 404) {
        navigate(`${routes.chooseBusiness}/1`);
        return;
      }

      updatedData = data;
      if (updatedData) {
        dispatch(setBusiness({ ...updatedData }));
        setSelectedBusiness(updatedData);
        fetchBusinessStats(business?.uuid);
        fetchPromotionPlans(business?.uuid);
        fetchBusinessOverallStats();
        fetchUserFeedbacks();
      }
    } catch (error) {
      toast.error('Unable to fetch business details');
    } finally {
      setSilentUpdate(false);
    }
  };

  const getCommunityDetails = useCallback(() => {
    const businessCommunities = business?.communities;
    if (!businessCommunities?.length) {
      return business?.onboardingCommunityDetails;
    }
    if (businessCommunities?.length === 1) {
      return businessCommunities[0];
    }
    if (businessCommunities?.length > 1) {
      const filteredAnyWhereCommunityList = businessCommunities?.filter(
        (community) => community?.isGlobal,
      );

      return !!filteredAnyWhereCommunityList?.length
        ? filteredAnyWhereCommunityList[0]
        : businessCommunities[0];
    }

    return businessCommunities[0];
  }, [business?.communities]);

  const communityDetails = getCommunityDetails();

  const shouldShowPreRegisterBanner =
    communityDetails?.preSignupEnabled &&
    business.preSignupDetails.preSignupStatus !== PreSignupStatusEnum.PROCESSED;

  const isAnyWhereCommunity = checkIsAnywhereCommunity(business);

  const onCancelPreregisterModal = () => {
    setShouldShowPreSignup(false);
    setPreSignupHeader('Pre-registration');
  };

  const onCancelActivateOfferModal = () => {
    setActivateOfferModal(null);
    setBusinessDetails();
  };

  const onCancelTransferDialog = () => {
    setShowTransferBalanceConfirmDialog(null);
  };

  const onInstantPayActivationSuccess = () => {
    setShowInstantpayTutorial(null);
    setBusinessDetails();
  };

  const onCloseModal = () => {
    setActivateOfferModal(null);
  };

  const deactivationTitleAndDescription = useMemo(() => {
    switch (showDeactiveConfirmDialog?.module?.label) {
      case 'Instant Pay':
        return {
          title: 'Are you sure you want to deactivate?',
          description:
            'If deactivated, customers will no longer be able to pay your business via Instant Pay.',
        };
      case 'List your business on Open Rewards':
        return {
          title: 'Are you sure you want to deactivate your business on Open Rewards?',
          description:
            'Your business will stop being visible on Open Rewards. Customers will no longer be able to earn or redeem rewards at your business',
        };
      default:
        return { title: 'Are you sure you want deactivate?', description: '' };
    }
  }, [showDeactiveConfirmDialog]);

  // Handle business selection after onboarding - directly use business data from navigation state
  useEffect(() => {
    if (fromOnboarding && businessDataFromOnboarding) {
      // Directly set the business from the onboarding data
      setSelectedBusiness(businessDataFromOnboarding);
      dispatch(setBusiness(businessDataFromOnboarding));

      if (businessDataFromOnboarding.uuid) {
        fetchDefaultAccount({ uuid: businessDataFromOnboarding.uuid });
        // Fetch full business details and stats
        fetchBusienss({ uuid: businessDataFromOnboarding.uuid }).then(({ data }) => {
          if (data) {
            dispatch(setBusiness({ ...data }));
            setSelectedBusiness(data);
            fetchBusinessStats(data.uuid);
            fetchPromotionPlans(data.uuid);
          }
        });
      }

      // Also fetch the business list in the background to update Redux store
      fetchBusinessList();

      // Clear the navigation state to prevent re-triggering
      navigate(routes.home, { replace: true, state: {} });
    } else if (fromOnboarding && businessUuidFromOnboarding && !businessDataFromOnboarding) {
      // Fallback: If only UUID is available, fetch the business list
      fetchBusinessList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromOnboarding, businessUuidFromOnboarding, businessDataFromOnboarding]);

  // Fallback: Select business from the list when coming from onboarding (if direct data wasn't available)
  useEffect(() => {
    if (
      isBusinessListSuccess &&
      businessListData &&
      fromOnboarding &&
      businessUuidFromOnboarding &&
      !businessDataFromOnboarding
    ) {
      dispatch(setBusinesses(businessListData || []));

      const newBusiness = businessListData.find(
        (b: EntityType) => b?.business?.uuid === businessUuidFromOnboarding,
      );

      if (newBusiness) {
        setSelectedBusiness({ ...newBusiness.business });
        dispatch(setBusiness({ ...newBusiness.business }));

        if (newBusiness.business.uuid) {
          fetchDefaultAccount({ uuid: newBusiness.business.uuid });
        }

        navigate(routes.home, { replace: true, state: {} });
      } else {
        // Business not found in list - redirect to choose business
        navigate(`${routes.chooseBusiness}/1`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isBusinessListSuccess,
    businessListData,
    fromOnboarding,
    businessUuidFromOnboarding,
    businessDataFromOnboarding,
  ]);

  useEffect(() => {
    if (business) {
      fetchBusinessStats(business?.uuid);
      fetchPromotionPlans(business?.uuid);
      fetchBusinessOverallStats();
      fetchUserFeedbacks();

      // Only fetch default account if business UUID exists
      if (business?.uuid) {
        fetchDefaultAccount({ uuid: business.uuid });
      }
    } else if (!fromOnboarding) {
      // Only redirect to choose-business if NOT coming from onboarding
      navigate(`${routes.chooseBusiness}/1`);
    }
  }, [business?.uuid, fromOnboarding]);

  useEffect(() => {
    // Skip initial setBusinessDetails if coming from onboarding - let onboarding handler set the business first
    if (!fromOnboarding) {
      setBusinessDetails();
    }
  }, []);

  useEffect(() => {
    if (isAccountsetupSuccess && defaultPaymentMethod) {
      dispatch(setDefaultAccount(defaultPaymentMethod));
    }
  }, [defaultPaymentMethod, isAccountsetupSuccess]);

  // This decides weather to show instant pay activation popup
  useEffect(() => {
    if (shouldShowinstantPayTutorial) {
      const instantPayModule = plans?.data?.filter((plan) => plan?.module?.label === 'Instant Pay');

      if (
        !!instantPayModule?.length &&
        instantPayModule[0]?.uuid &&
        instantPayModule[0]?.status !== PlanActivationStatus.ACTIVATED
      ) {
        activateInstantPay(instantPayModule[0]?.uuid);
        // Clear the state so it doesn't persist on refresh
        navigate(location.pathname, { replace: true, state: null });
      } else if (instantPayModule[0]?.status === PlanActivationStatus.ACTIVATED) {
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [shouldShowinstantPayTutorial, plans]);

  const onBusinessPusher = () => {
    setBusinessDetails();
  };
  const onTransactionUpdate = () => {
    setSilentUpdate(true);
    fetchBusinessStats(business?.uuid);
  };

  usePusher(
    [
      {
        eventName: 'REACTIVATED',
        eventCallback: onBusinessPusher,
      },
    ],
    `business-${business?.uuid}`,
    [business?.uuid],
  );

  usePusher(
    [
      {
        eventName: 'ACTIVATED',
        eventCallback: onBusinessPusher,
      },
    ],
    `business-${business?.uuid}`,
    [business?.uuid],
  );

  usePusher(
    [
      {
        eventName: 'TRANSACTION_UPDATED',
        eventCallback: onTransactionUpdate,
      },
    ],
    `business-${business?.uuid}`,
    [business?.uuid],
  );

  const isLoading = useMemo(
    () =>
      !silentUpdate &&
      (businessStats?.status === FETCH_STATES.LOADING ||
        plans?.status === FETCH_STATES.LOADING ||
        businessOverallStats?.status === FETCH_STATES.LOADING ||
        userFeedback?.status === FETCH_STATES.LOADING ||
        isFetching),
    [
      silentUpdate,
      businessStats?.status,
      plans?.status,
      businessOverallStats?.status,
      userFeedback?.status,
    ],
  );
  useEffect(() => {
    if (business) {
      const showPreRegisterPopup =
        communityDetails?.preSignupEnabled &&
        !(
          preSignupStatus === PreSignupStatusEnum.ENROLLED ||
          preSignupStatus === PreSignupStatusEnum.PROCESSED
        );
      if (!isLoading) {
        setShouldShowPreSignup(showPreRegisterPopup);
      }
    }
  }, [business, isLoading]);

  return (
    <>
      <div className="bg-white h-screen flex flex-col overflow-hidden">
        <Render when={!!business}>
          {/* Header - Fixed */}
          <div className="p-8 pt-6 pb-0 flex-shrink-0">
            <BusinessHeader selectedBusiness={business} />
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-8 pt-5">
            <div className="flex flex-col gap-5">
              <Render when={isLoading}>
                <div className="flex justify-center items-center h-[60vh]">
                  <Loader size={8} color={'darkBlue'} />
                </div>
              </Render>
              <Render when={!isLoading}>
                {/* Business Stats Cards - Top Row */}
                <Render when={isAnyBusinessActivity}>
                  <BusinessStats businessStats={businessStats?.data} />
                </Render>

                <Render
                  when={
                    !!businessOverallStats?.data?.userFeedbacks ||
                    !!businessOverallStats?.data?.positiveInfluence
                  }
                >
                  <UserFeedbackTiles
                    businessOverallStats={businessOverallStats?.data}
                    isPlanActive={plans?.data?.[0]?.status === PlanActivationStatus.ACTIVATED}
                  />
                </Render>

                {/* Rewards Fund Balance Card */}
                <Render when={businessStats?.status === FETCH_STATES.SUCECSS}>
                  <TopupDetails
                    setShowTransferDialog={(transferAmount: number) =>
                      setShowTransferBalanceConfirmDialog({
                        transferAmount,
                      })
                    }
                    businessStats={businessStats?.data}
                    business={business}
                    plans={plans?.data}
                  />
                </Render>
              </Render>
            </div>
          </div>
        </Render>
      </div>

      {/* Transfer Balance Modal */}
      <Render when={!!showTransferBalanceConfirmDialog}>
        <ModalComponent
          containerClass="md:w-[520px] max-h[80vh] w-[90vw]"
          onClose={() => setShowTransferBalanceConfirmDialog(null)}
        >
          <TransferConfirmationModal
            onTransfer={onTransferConfirm}
            onCancel={onCancelTransferDialog}
            walletBalance={businessStats?.data?.walletBalance}
            transferringAmount={transferringAmount}
          />
        </ModalComponent>
      </Render>
    </>
  );
};

export default Home;
